import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowLeft, LayoutDashboard, RotateCcw, Loader2 } from 'lucide-react';
import { clearGuidedTourSeen } from './shared/GuidedTour';
import { useTranslation } from 'react-i18next';

const DEMO_TENANT_CARD = 'CONDOCARD-a-0001';
const PROPERTY_DEMO_ORG_ID = 'condo-demo-org-0001';

// Root-level demo controls for the Tenant Portal (/assistant routes).
// - "Back to showroom": appears when a showroom return URL is set.
// - "Back to Manager": appears on the demo tenant card so the showroom can
//   cycle Tenant -> Manager. It establishes an isolated manager demo session
//   via /demo/manager (which resumes, never overwrites), not browser history.
// - "Reset demo": the ONE way to start the property demo over — closes anything
//   left open and drops this browser's demo tenant so registration shows again.
export default function ShowroomBackButton() {
    const { pathname } = useLocation();
    const { t } = useTranslation();
    const [resetting, setResetting] = useState(false);
    if (!pathname.startsWith('/assistant')) return null;
    const ret = typeof window !== 'undefined' ? sessionStorage.getItem('yulontap_demo_return') : null;
    const isDemoTenant = pathname.includes(DEMO_TENANT_CARD);
    if (!ret && !isDemoTenant) return null;

    const doReset = async () => {
        if (resetting) return;
        if (!window.confirm(t('propertyDemo.resetConfirm',
            'This clears all demo alerts and activity. Real buildings are not affected.'))) return;
        setResetting(true);
        try {
            // Proof that the caller is inside the demo: the demo building's own
            // resident session token. A real building's account cannot reset.
            let demoToken = '';
            try {
                const stay = JSON.parse(localStorage.getItem(`yulontap_stay:${PROPERTY_DEMO_ORG_ID}`)
                    || localStorage.getItem(`yulontap_assistant:${DEMO_TENANT_CARD}`) || '{}');
                demoToken = stay.token || '';
            } catch (_e) { /* ignore */ }
            const res = await fetch(`${(typeof window !== 'undefined' ? window.location.origin : '')}/api/demo-condo/reset`, {
                method: 'POST', headers: { 'X-Demo-Session': demoToken },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            // Only the DEMO org's keys — a real resident's stay for another
            // building in this same browser is never touched.
            try {
                localStorage.removeItem(`yulontap_stay:${PROPERTY_DEMO_ORG_ID}`);
                localStorage.removeItem(`yulontap_assistant:${DEMO_TENANT_CARD}`);
                localStorage.removeItem(`yulontap_req_cleared:${DEMO_TENANT_CARD}`);
                Object.keys(localStorage)
                    .filter((k) => k.startsWith('yot_tap_session:condo-demo')
                        || k.startsWith('yot_conv_CONDOCARD')
                        || k.startsWith('yulontap_req_cleared:CONDOCARD'))
                    .forEach((k) => localStorage.removeItem(k));
                sessionStorage.removeItem('yulontap_property_demo_offer_tenant');
                // A fresh demo should feel like a first visit: replay the guided tour.
                clearGuidedTourSeen();
            } catch (_e) { /* ignore */ }
            window.location.replace(`/demo/tenant${ret ? `?return_url=${encodeURIComponent(ret)}` : ''}`);
        } catch (_e) { setResetting(false); }
    };

    return (
        <>
            {ret && (
                <button
                    onClick={() => { sessionStorage.removeItem('yulontap_demo_return'); window.location.href = ret; }}
                    data-testid="showroom-back"
                    className="fixed top-3 left-3 z-[9999] inline-flex items-center gap-2 text-sm text-white bg-black/70 hover:bg-black/85 border border-white/20 rounded-full px-3 py-1.5 backdrop-blur transition-colors">
                    <ArrowLeft className="w-4 h-4" />{t('demo.backToShowroom', 'Back to showroom')}
                </button>
            )}
            {isDemoTenant && (
                <>
                    <button
                        onClick={() => {
                            const q = ret ? `?return_url=${encodeURIComponent(ret)}` : '';
                            window.location.href = `/demo/manager${q}`;
                        }}
                        data-testid="demo-back-to-manager"
                        className="fixed top-16 right-3 z-[9999] inline-flex items-center gap-2 text-sm text-white bg-emerald-600/90 hover:bg-emerald-500 border border-emerald-300/30 rounded-full px-3 py-1.5 backdrop-blur transition-colors">
                        <LayoutDashboard className="w-4 h-4" />{t('demo.backToManager', 'Back to Manager')}
                    </button>
                    <button
                        onClick={doReset}
                        disabled={resetting}
                        title={t('propertyDemo.resetDemo', 'Reset demo')}
                        aria-label={t('propertyDemo.resetDemo', 'Reset demo')}
                        data-testid="demo-reset-property"
                        className="fixed top-[6.5rem] right-3 z-[9999] inline-flex items-center gap-2 text-sm text-slate-200 bg-black/70 hover:bg-black/85 hover:text-white border border-white/20 rounded-full px-3 py-1.5 backdrop-blur transition-colors disabled:opacity-50">
                        {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                        <span className="hidden xl:inline">{t('propertyDemo.resetDemo', 'Reset demo')}</span>
                    </button>
                </>
            )}
        </>
    );
}
